import {
  Avatar,
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import { styled } from '@mui/system';
import { Phone } from 'lucide-react';
import { CallType, CallMode } from '../../routes/agent/RolePlay';

// Styled components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: '#C11919', // Prudential red
  boxShadow: theme.shadows[1],
}));

const StatusDot = styled('div')(({ active }: { active: boolean }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: active ? '#4caf50' : '#b0bec5',
  marginRight: 8,
}));

// Call badge styles
const getCallBadgeColor = (callType: CallType) => {
  return callType === CallType.INITIAL ? 'primary' : 'error';
};

const getCallBadgeText = (callType: CallType) => {
  return callType === CallType.INITIAL
    ? 'Initial Consultation'
    : 'Follow-up Consultation';
};

interface ActiveCallHeaderProps {
  agentInfo: any;
  callType: CallType;
  callMode?: CallMode;
  callDuration: string;
  isSpeaking: boolean;
  accentDisplay?: string;
  onEndCall: () => void;
}

export const ActiveCallHeader = ({
  agentInfo,
  callType,
  callMode,
  callDuration,
  isSpeaking,
  accentDisplay,
  onEndCall,
}: ActiveCallHeaderProps) => {
  return (
    <StyledAppBar position="static">
      <Toolbar>
        <Box display="flex" alignItems="center">
          <Box position="relative" mr={1.5}>
            <Avatar
              src={
                agentInfo?.profile?.avatarUrl || agentInfo?.profile?.avatar ||
                'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp'
              }
              alt={agentInfo?.profile?.name || 'Rachel'}
            />
            {isSpeaking && (
              <Box
                position="absolute"
                bottom={0}
                right={0}
                width={10}
                height={10}
                bgcolor="success.main"
                borderRadius="50%"
                border="2px solid white"
              />
            )}
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={500}>
              {agentInfo?.profile?.name || 'Insurance Advisor'}
            </Typography>
            <Box display="flex" alignItems="center">
              <StatusDot active={true} />
              <Typography variant="caption" color="rgba(255,255,255,0.8)">
                On call • {callDuration}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box flexGrow={1} />

        <Box display="flex" alignItems="center" gap={1}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            mr={1}
          >
            <Chip
              label={callMode === CallMode.SCRIPTED ? 'Annual Check-In' : getCallBadgeText(callType)}
              color={callMode === CallMode.SCRIPTED ? 'warning' : getCallBadgeColor(callType)}
              size="small"
            />
            {accentDisplay && (
              <Chip
                label={`${accentDisplay} Accent`}
                color="secondary"
                size="small"
                variant="outlined"
                sx={{ mt: 0.5, bgcolor: 'rgba(255,255,255,0.2)' }}
              />
            )}
          </Box>
          <IconButton
            color="inherit"
            onClick={onEndCall}
            sx={{
              bgcolor: 'rgba(0,0,0,0.3)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.4)',
              },
            }}
          >
            <Phone size={20} style={{ transform: 'rotate(135deg)' }} />
          </IconButton>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};
